import React, { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import MobileAppLayout from "../../layouts/MobileAppLayout";
import { GameIcon, Skeleton } from "../../components/game";
import { resolveMediaUrl } from "../student/quiz/media";
import api from "../../services/api";
import { formatDate } from "../../lib/dateFormat";

type ProfileResponse = {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  city: string | null;
  club: { id: string; name: string; city: string | null } | null;
  team: { id: string; name: string; sport_type: string | null; age_group: string | null } | null;
  teams?: { id: string; name: string; sport_type: string | null; age_group: string | null }[];
  sport: string | null;
  age_group: string | null;
  stats: {
    points_total: number;
    club_rank: number | null;
    level?: {
      level: number;
      title: string;
      progress_pct: number;
      xp_into_level: number;
      xp_for_next: number;
    };
  };
  badges: {
    id: string;
    code: string;
    name: string;
    description: string;
    icon_emoji: string | null;
    icon_image_url: string | null;
    color: string;
    earned_at: string;
  }[];
  recent_prizes: {
    id: string;
    period_type: "week" | "month";
    scope: "club" | "platform";
    rank: number;
    points_awarded: number;
    awarded_at: string;
  }[];
};

type Insights = {
  quizzes_completed: number;
  total_answers: number;
  correct_answers: number;
  accuracy: number | null;
  avg_response_ms: number | null;
  speed_percentile: number | null;
  difficulty: { easy: number; medium: number; hard: number; preferred: string | null };
  attendance: { present: number; total: number; rate: number | null };
  videos_watched: number;
  sports: string[];
};

const TONE: Record<string, string> = {
  amber: "bg-amber-100",
  indigo: "bg-indigo-100",
  emerald: "bg-emerald-100",
  violet: "bg-violet-100",
  sky: "bg-sky-100",
  rose: "bg-rose-100",
  teal: "bg-teal-100",
};

/**
 * Athlete profile page. Own profile at /app/profile, others at
 * /app/profile/:userId. Curated data from GET /profiles/:userId plus real
 * derived insights from GET /profiles/:userId/insights.
 */
export default function AppProfile() {
  const { t } = useTranslation();
  const { userId: routeUserId } = useParams<{ userId?: string }>();
  const { user } = useAuth();

  const difLabel = (d: string) =>
    d === "easy" ? t("profile.difEasy", "ușoare") : d === "medium" ? t("profile.difMedium", "medii") : t("profile.difHard", "grele");
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [insights, setInsights] = useState<Insights | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(false);

  const targetId = routeUserId || user?.id;

  useEffect(() => {
    if (!targetId) return;
    (async () => {
      setLoading(true);
      setError(null);
      setInsights(null);
      try {
        const { data } = await api.get<ProfileResponse>(`/profiles/${targetId}`);
        setProfile(data);
      } catch (e: any) {
        setError(e?.response?.data?.error || t("profile.unavailable", "Profil indisponibil"));
      } finally {
        setLoading(false);
      }
      // Insights are best-effort — never block the profile on them.
      api
        .get<Insights>(`/profiles/${targetId}/insights`)
        .then((r) => setInsights(r.data))
        .catch(() => setInsights(null));
    })();
  }, [targetId]);

  if (loading) {
    return (
      <MobileAppLayout>
        <div className="pb-24 animate-fadeIn">
          <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 px-4 pt-6 pb-16">
            <div className="flex flex-col items-center text-center">
              <Skeleton className="w-28 h-28 rounded-full bg-white/30" />
              <Skeleton className="h-7 w-44 rounded-lg mt-3 bg-white/30" />
              <Skeleton className="h-4 w-32 rounded-md mt-2 bg-white/20" />
            </div>
          </div>
          <div className="px-4 -mt-10">
            <div className="grid grid-cols-3 gap-3">
              <Skeleton className="h-20 rounded-2xl" />
              <Skeleton className="h-20 rounded-2xl" />
              <Skeleton className="h-20 rounded-2xl" />
            </div>
          </div>
        </div>
      </MobileAppLayout>
    );
  }

  if (error || !profile) {
    return (
      <MobileAppLayout>
        <div className="p-6 text-center text-slate-500">{error || t("profile.loadError", "Nu s-au putut încărca datele")}</div>
      </MobileAppLayout>
    );
  }

  const initial = profile.full_name?.charAt(0)?.toUpperCase() || "?";
  const avatar = resolveMediaUrl(profile.avatar_url);
  const lvl = profile.stats.level;

  // Build the "real data" insight lines — only include what we truly know.
  const items: { emoji: string; tone: string; text: React.ReactNode }[] = [];
  if (insights) {
    if (insights.speed_percentile != null && insights.speed_percentile >= 50)
      items.push({ emoji: "⚡", tone: "amber", text: <Trans i18nKey="profile.insightSpeed" defaults="Răspunde mai rapid decât <b>{{pct}}%</b> dintre colegi" values={{ pct: insights.speed_percentile }} components={{ b: <b /> }} /> });
    if (insights.difficulty.preferred)
      items.push({ emoji: "🎯", tone: "indigo", text: <Trans i18nKey="profile.insightDifficulty" defaults="Preferă, în general, quiz-urile <b>{{level}}</b>" values={{ level: difLabel(insights.difficulty.preferred) }} components={{ b: <b /> }} /> });
    if (insights.accuracy != null && insights.total_answers >= 5)
      items.push({ emoji: "✅", tone: "emerald", text: <Trans i18nKey="profile.insightAccuracy" defaults="<b>{{pct}}%</b> răspunsuri corecte ({{correct}}/{{total}})" values={{ pct: insights.accuracy, correct: insights.correct_answers, total: insights.total_answers }} components={{ b: <b /> }} /> });
    if (insights.quizzes_completed > 0)
      items.push({ emoji: "📚", tone: "violet", text: <Trans i18nKey="profile.insightQuizzes" defaults="A rezolvat <b>{{count}}</b> {{unit}}" values={{ count: insights.quizzes_completed, unit: insights.quizzes_completed === 1 ? t("profile.quizOne", "quiz") : t("profile.quizMany", "quiz-uri") }} components={{ b: <b /> }} /> });
    if (insights.attendance.total > 0)
      items.push({
        emoji: "🏃",
        tone: "sky",
        text:
          insights.attendance.rate != null && insights.attendance.rate >= 70 ? (
            <Trans i18nKey="profile.insightAttendanceGood" defaults="Participă constant la antrenamente (<b>{{present}}/{{total}}</b>)" values={{ present: insights.attendance.present, total: insights.attendance.total }} components={{ b: <b /> }} />
          ) : (
            <Trans i18nKey="profile.insightAttendancePartial" defaults="A participat la <b>{{present}}</b> din {{total}} antrenamente" values={{ present: insights.attendance.present, total: insights.attendance.total }} components={{ b: <b /> }} />
          ),
      });
    if (insights.videos_watched > 0)
      items.push({ emoji: "🎬", tone: "rose", text: <Trans i18nKey="profile.insightVideos" defaults="A vizionat <b>{{count}}</b> {{unit}}" values={{ count: insights.videos_watched, unit: insights.videos_watched === 1 ? t("profile.videoOne", "material video") : t("profile.videoMany", "materiale video") }} components={{ b: <b /> }} /> });
    if (insights.sports.length)
      items.push({ emoji: "⚽", tone: "teal", text: <Trans i18nKey="profile.insightSports" defaults="Joacă <b>{{sports}}</b>" values={{ sports: insights.sports.join(", ") }} components={{ b: <b /> }} /> });
  }

  return (
    <MobileAppLayout>
      <div className="pb-24">
        {/* Hero */}
        <div className="relative bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 px-4 pt-6 pb-16 text-white">
          {!routeUserId && (
            <Link
              to="/app/settings"
              aria-label={t("profile.settings", "Setări")}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 backdrop-blur-sm grid place-items-center text-white active:scale-95 transition"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </Link>
          )}

          <div className="flex flex-col items-center text-center">
            <button
              type="button"
              onClick={() => avatar && setZoom(true)}
              className="relative rounded-full focus:outline-none"
              aria-label={t("profile.enlargeImage", "Mărește imaginea")}
            >
              {avatar ? (
                <img
                  src={avatar}
                  alt={profile.full_name || ""}
                  className="w-28 h-28 rounded-full object-cover border-4 border-white/50 shadow-xl active:scale-95 transition"
                />
              ) : (
                <div className="w-28 h-28 rounded-full bg-white/20 border-4 border-white/50 flex items-center justify-center text-4xl font-bold shadow-xl">
                  {initial}
                </div>
              )}
              {/* Level badge on the avatar */}
              {lvl && (
                <span className="absolute -bottom-1 -right-1 min-w-8 h-8 px-1.5 rounded-full bg-amber-400 text-amber-900 text-sm font-extrabold grid place-items-center border-2 border-white shadow-md tabular-nums">
                  {lvl.level}
                </span>
              )}
            </button>

            <h1 className="text-2xl font-extrabold mt-3">{profile.full_name || t("profile.athlete", "Sportiv")}</h1>
            {profile.teams && profile.teams.length > 0 ? (
              <p className="text-white/85 text-sm mt-1 flex flex-wrap items-center justify-center gap-x-1.5">
                {profile.teams.map((tm, i) => (
                  <React.Fragment key={tm.id}>
                    {i > 0 && <span className="text-white/40">·</span>}
                    <Link to={`/app/team/${tm.id}`} className="font-semibold underline decoration-white/40 underline-offset-2 active:opacity-80">
                      {tm.name}
                    </Link>
                  </React.Fragment>
                ))}
                {profile.sport && <span className="text-white/70">· {profile.sport}</span>}
              </p>
            ) : profile.team ? (
              <p className="text-white/85 text-sm mt-1">
                <Link to={`/app/team/${profile.team.id}`} className="font-semibold underline decoration-white/40 underline-offset-2 active:opacity-80">
                  {profile.team.name}
                </Link>
                {profile.sport && ` · ${profile.sport}`}
              </p>
            ) : null}
            {profile.club && (
              <p className="text-white/70 text-xs mt-0.5">
                <Link to={`/app/club/${profile.club.id}`} className="font-semibold underline decoration-white/40 underline-offset-2 active:opacity-80">
                  {profile.club.name}
                </Link>
                {profile.club.city && ` · ${profile.club.city}`}
              </p>
            )}
          </div>
        </div>

        {/* Stats overlap — 3 cards */}
        <div className="relative z-10 px-4 -mt-10">
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white rounded-2xl shadow-md border border-slate-100 p-3 text-center">
              <div className="w-8 h-8 mx-auto mb-1 rounded-lg bg-amber-100 grid place-items-center">
                <GameIcon name="star" className="w-4 h-4 text-amber-600" />
              </div>
              <p className="text-xl font-extrabold text-amber-600 tabular-nums leading-none">{profile.stats.points_total}</p>
              <p className="text-[10px] text-slate-500 mt-1">{t("profile.points", "Puncte")}</p>
            </div>
            <div className="bg-white rounded-2xl shadow-md border border-slate-100 p-3 text-center">
              <div className="w-8 h-8 mx-auto mb-1 rounded-lg bg-indigo-100 grid place-items-center">
                <GameIcon name="medal" className="w-4 h-4 text-indigo-600" />
              </div>
              <p className="text-xl font-extrabold text-indigo-600 tabular-nums leading-none">
                {profile.stats.club_rank ? `#${profile.stats.club_rank}` : "—"}
              </p>
              <p className="text-[10px] text-slate-500 mt-1">{t("profile.clubRank", "Rang club")}</p>
            </div>
            <div className="bg-white rounded-2xl shadow-md border border-slate-100 p-3 text-center">
              <div className="w-8 h-8 mx-auto mb-1 rounded-lg bg-emerald-100 grid place-items-center">
                <GameIcon name="bulb" className="w-4 h-4 text-emerald-600" />
              </div>
              <p className="text-xl font-extrabold text-emerald-600 tabular-nums leading-none">
                {insights ? insights.quizzes_completed : "—"}
              </p>
              <p className="text-[10px] text-slate-500 mt-1">{t("profile.quizzes", "Quiz-uri")}</p>
            </div>
          </div>

          {/* Level bar */}
          {lvl && (
            <div className="bg-white rounded-2xl shadow-md border border-slate-100 p-3.5 mt-3 flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl grid place-items-center font-extrabold text-white bg-gradient-to-br from-indigo-500 to-blue-600 shadow-[0_2px_0_#3730a3] shrink-0">
                {lvl.level}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-bold text-slate-700 truncate">{lvl.title}</span>
                  <span className="text-[11px] text-slate-400 font-bold tabular-nums">
                    {lvl.xp_into_level}/{lvl.xp_for_next} XP
                  </span>
                </div>
                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all"
                    style={{ width: `${lvl.progress_pct}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Settings entry (own profile only) */}
          {!routeUserId && (
            <Link
              to="/app/settings"
              className="mt-3 flex items-center gap-3 bg-white rounded-2xl shadow-md border border-slate-100 p-3.5 active:translate-y-[1px] transition"
            >
              <span className="w-10 h-10 rounded-xl bg-slate-100 grid place-items-center text-slate-600 shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-slate-800">{t("profile.settings", "Setări")}</p>
                <p className="text-xs text-slate-500">{t("profile.settingsDesc", "Cont, notificări, limbă")}</p>
              </div>
              <svg className="w-5 h-5 text-slate-400 shrink-0" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          )}
        </div>

        {/* Insights (real data) */}
        {items.length > 0 && (
          <div className="px-4 mt-6">
            <h2 className="text-sm font-bold text-slate-700 mb-2">{t("profile.about", "Despre {{name}}", { name: profile.full_name?.split(" ")[0] || t("profile.athleteLower", "sportiv") })}</h2>
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 divide-y divide-slate-50">
              {items.map((it, i) => (
                <div key={i} className="flex items-center gap-3 p-3.5">
                  <span className={`w-9 h-9 rounded-xl grid place-items-center text-lg shrink-0 ${TONE[it.tone]}`}>{it.emoji}</span>
                  <p className="text-sm text-slate-700 leading-snug">{it.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Badges */}
        <div className="px-4 mt-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-bold text-slate-700">{t("profile.badges", "Insigne")}</h2>
            {!routeUserId && (
              <Link to="/app/badges" className="text-xs font-semibold text-violet-600 hover:underline">
                {t("profile.viewAll", "Vezi toate →")}
              </Link>
            )}
          </div>
          {profile.badges.length === 0 ? (
            <div className="bg-slate-50 rounded-2xl p-6 text-center text-sm text-slate-500">
              {t("profile.noBadges", "Nicio insignă câștigată încă. Continuă cu quiz-urile!")}
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {profile.badges.map((b) => (
                <div key={b.id} className="bg-white rounded-2xl border border-slate-200 p-3 text-center" title={b.description}>
                  <div
                    className="w-12 h-12 rounded-full mx-auto flex items-center justify-center text-2xl mb-2 overflow-hidden"
                    style={{ backgroundColor: `${b.color}20`, color: b.color }}
                  >
                    {b.icon_image_url ? (
                      <img src={b.icon_image_url} alt={b.name} className="w-full h-full object-contain p-1.5" />
                    ) : (
                      b.icon_emoji || "🏆"
                    )}
                  </div>
                  <p className="text-xs font-medium text-slate-800 truncate">{b.name}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent prizes */}
        {profile.recent_prizes.length > 0 && (
          <div className="px-4 mt-6">
            <h2 className="text-sm font-bold text-slate-700 mb-2">{t("profile.recentPrizes", "Premii recente")}</h2>
            <div className="space-y-2">
              {profile.recent_prizes.map((p) => (
                <div key={p.id} className="bg-white rounded-2xl border border-slate-200 p-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center text-xl">
                    {p.rank === 1 ? "🥇" : p.rank === 2 ? "🥈" : "🥉"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800">
                      {t("profile.rankPlace", "Locul {{rank}}", { rank: p.rank })} · {p.scope === "platform" ? t("profile.scopePlatform", "Platformă") : t("profile.scopeClub", "Club")}
                    </p>
                    <p className="text-xs text-slate-500">
                      {p.period_type === "week" ? t("profile.periodWeek", "Săptămâna") : t("profile.periodMonth", "Luna")} · {t("profile.pointsAwarded", "+{{count}} puncte", { count: p.points_awarded })}
                    </p>
                  </div>
                  <p className="text-xs text-slate-400">{formatDate(new Date(p.awarded_at))}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Avatar zoom */}
      {zoom && avatar && (
        <div
          className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-sm flex items-center justify-center p-6 animate-fadeIn"
          onClick={() => setZoom(false)}
        >
          <img
            src={avatar}
            alt={profile.full_name || ""}
            className="max-w-full max-h-full rounded-2xl object-contain shadow-2xl animate-zoom"
          />
          <button
            onClick={() => setZoom(false)}
            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/15 text-white grid place-items-center"
            aria-label={t("profile.close", "Închide")}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    </MobileAppLayout>
  );
}
